import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useGuardedNavigate } from "../context/AuthModalContext";
import { useTranslation } from "../context/TranslationContext";
import { getSiteContent } from "../api/content";
import { CountdownDisplay } from "../components/CountdownDisplay";
import { MintlyFreeForeverSection } from "../components/MintlyFreeForeverSection";
import BotProtectionSection from "../components/BotProtectionSection";
import { getMintSpaceAppStoreCatalog } from "../data/mintSpaceAppStoreCatalog";
import AppIcon from "../components/ds/AppIcon";
import Badge from "../components/ds/Badge";
import Button from "../components/ds/Button";
import Icon from "../components/ds/Icon";
import Page from "../components/ds/Page";

function videoEmbedUrl(url) {
  if (!url) return null;
  const u = String(url).trim();
  const yt = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s?]+)/i);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  if (u.includes("youtube.com/embed")) return u.split("?")[0];
  const vimeo = u.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return u;
}

const sortByOrder = (arr) =>
  [...(arr || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const AboutPage = ({ setToast }) => {
  const { t } = useTranslation();
  const navigate = useGuardedNavigate();
  const location = useLocation();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);

  const storeApps = useMemo(() => getMintSpaceAppStoreCatalog(t), [t]);

  // The two sections still on the old look take their colours as props.
  const goCreateCampaign = () => navigate("/create-business");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getSiteContent();
        if (!cancelled && res.success) setSite(res.data);
      } catch (e) {
        if (!cancelled) setToast?.(e?.response?.data?.message || t("resources.loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setToast, t]);

  // A link to a section (#bot-protection, from the old Bot protection page) scrolls to it
  // once the page has content to scroll through.
  useEffect(() => {
    if (loading || !location.hash) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loading, location.hash]);

  const title = site?.aboutTitle?.trim() || t("resources.aboutTitle");
  const body = site?.aboutMission?.trim() || t("resources.aboutMissionFallback");

  const landingVideoRaw = site?.landingVideoUrl?.trim() || "";
  const isYoutubeVimeo = /youtube\.com|youtu\.be|vimeo\.com/i.test(landingVideoRaw);
  const landingIframeSrc = landingVideoRaw && isYoutubeVimeo ? videoEmbedUrl(landingVideoRaw) : null;
  const useNativeVideo =
    landingVideoRaw &&
    !isYoutubeVimeo &&
    (/\.(mp4|webm|ogg)(\?|$)/i.test(landingVideoRaw) || /backblazeb2\.com|b-cdn\.net/i.test(landingVideoRaw));
  const bm = sortByOrder(site?.businessModels);

  const video = (props) => <video src={landingVideoRaw} className="h-full w-full bg-black object-contain" controls playsInline preload="metadata" {...props} />;
  const sectionHead = (title, subtitle) => (
    <div className="flex flex-col gap-space-16">
      <h2 className="text-title-1 text-label-primary">{title}</h2>
      {subtitle && <p className="max-w-[720px] text-subheadline text-label-secondary">{subtitle}</p>}
    </div>
  );

  return (
    <Page>
      <Button
        variant="plain"
        size="s"
        icon={<Icon name="arrow-left" size={16} />}
        className="self-start"
        onClick={() => navigate("/resources")}
      >
        {t("resources.backToResources")}
      </Button>

      {loading ? (
        <p className="text-subheadline text-label-secondary">{t("common.loading")}</p>
      ) : (
        <div className="flex flex-col gap-space-48 lg:gap-[88px]">
          <article className="flex flex-col gap-space-16">
            <h1 className="text-title-1 text-label-primary min-[700px]:text-large-title">{title}</h1>
            <div className="whitespace-pre-wrap text-body text-label-secondary">{body}</div>
          </article>

          {landingVideoRaw && (
            <section className="aspect-video w-full overflow-hidden rounded-radius-lg bg-black">
              {useNativeVideo || !landingIframeSrc ? (
                video()
              ) : (
                <iframe
                  title="Mintly"
                  src={landingIframeSrc}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </section>
          )}

          {(site?.waitlistTargetAt || site?.officialLaunchTargetAt) && (
            <section className="grid grid-cols-1 gap-space-16 min-[700px]:grid-cols-2 min-[700px]:gap-space-24">
              {site?.waitlistTargetAt && (
                <CountdownDisplay label={site?.waitlistLabel || t("resources.waitlistLabel")} targetAt={site.waitlistTargetAt} />
              )}
              {site?.officialLaunchTargetAt && (
                <CountdownDisplay
                  label={site?.officialLaunchLabel || t("resources.officialLaunchLabel")}
                  targetAt={site.officialLaunchTargetAt}
                />
              )}
            </section>
          )}

          {bm.length > 0 && (
            <section className="flex flex-col gap-space-24">
              {sectionHead(site?.businessModelsSectionTitle || t("resources.businessModelsTitle"), t("resources.businessModelsSubtitle"))}
              <div className="grid grid-cols-1 gap-space-16 min-[500px]:grid-cols-2 lg:grid-cols-3 lg:gap-space-24 xl:grid-cols-4">
                {bm.map((item, idx) => (
                  <div key={`${item.title}-${idx}`} className="flex flex-col gap-space-12 rounded-radius-lg bg-bg-raised p-space-16">
                    {item.logoUrl ? (
                      <img src={item.logoUrl} alt="" className="size-6 object-contain" />
                    ) : (
                      <Icon name="apps" size={24} className="text-label-secondary" />
                    )}
                    <h3 className="text-headline text-label-primary">{item.title}</h3>
                    {item.subtitle && <p className="text-footnote text-label-secondary">{item.subtitle}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="flex flex-col gap-space-24">
            {sectionHead(t("resources.ourAppsTitle"), t("resources.appStoreBlurb"))}
            <div className="flex flex-wrap gap-space-24">
              {storeApps.map((app) => (
                <div key={app.id} className={`flex w-[104px] flex-col items-center gap-[10px] ${app.comingSoon ? "opacity-70" : ""}`}>
                  <AppIcon app={app.id} />
                  <p className="text-center text-footnote-semibold text-label-primary">{app.name}</p>
                  {app.comingSoon && (
                    <Badge size="s" tone="success">
                      {t("resources.comingSoon")}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
            <Button size="m" variant="gray" className="self-start" onClick={goCreateCampaign}>
              {t("resources.getStarted")}
            </Button>
          </section>

          <section id="bot-protection" className="scroll-mt-24">
            <BotProtectionSection />
          </section>

          <MintlyFreeForeverSection />

          <footer role="contentinfo" className="border-t border-separator-default pt-space-32 text-center text-caption-1 text-label-tertiary">
            {t("common.copyrightFooter")}
          </footer>
        </div>
      )}
    </Page>
  );
};

export default AboutPage;
